import React from 'react';
import { Eye, Sparkles, BookOpen, Calculator, Microscope, Brain, Star } from 'lucide-react';
import { Database } from '../lib/supabase';
import { useTheme } from '../hooks/useTheme';

type AIActivity = Database['public']['Tables']['ai_generated_activities']['Row'];

export interface AIActivityCardProps {
  activity: AIActivity;
  onStart?: () => void;
  onViewContents?: () => void;
  completed?: boolean;
  showViewContents?: boolean;
}

const subjectIcons = {
  math: Calculator,
  reading: BookOpen,
  science: Microscope,
};

const subjectColors = {
  math: 'blue',
  reading: 'green',
  science: 'purple',
};

const difficultyLabels = {
  1: 'Beginner',
  2: 'Easy',
  3: 'Medium',
  4: 'Hard',
  5: 'Expert',
};

export function AIActivityCard({ activity, onStart, onViewContents, completed = false, showViewContents = false }: AIActivityCardProps) {
  const { theme } = useTheme();
  
  // Safety check for activity data after hooks
  if (!activity) {
    console.warn('AIActivityCard: activity prop is undefined or null');
    return null;
  }

  const IconComponent = subjectIcons[activity.subject as keyof typeof subjectIcons] || BookOpen;
  const colorClass = subjectColors[activity.subject as keyof typeof subjectColors] || 'blue';

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just created';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const handleButtonClick = () => {
    if (showViewContents && onViewContents) {
      onViewContents();
    } else if (onStart) {
      onStart();
    }
  };

  return (
    <div className={`rounded-xl shadow-md border overflow-hidden hover:shadow-lg transition-shadow duration-200 flex flex-col h-full relative ${
      theme.mode === 'high-contrast'
        ? 'bg-white border-black'
        : 'bg-[var(--bg-secondary)] border-[var(--border-color)]'
    }`}>
      {/* AI Badge */}
      <div className="absolute top-3 right-3 z-10">
        <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
          theme.mode === 'high-contrast'
            ? 'bg-purple-600 text-white'
            : 'bg-gradient-to-r from-purple-500 to-blue-500 text-white'
        }`}>
          <Brain className="w-3 h-3" />
          <span>AI</span>
        </div>
      </div>

      <div className={`h-2 ${
        theme.mode === 'high-contrast'
          ? `bg-[var(--accent-${colorClass})]`
          : `bg-gradient-to-r from-${colorClass}-500 to-${colorClass}-600`
      }`}></div>
      
      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3 flex-1 min-w-0 pr-12">
            <div className={`p-3 rounded-lg flex-shrink-0 ${
              theme.mode === 'high-contrast'
                ? `bg-[var(--accent-${colorClass})]`
                : `bg-gradient-to-r from-${colorClass}-500 to-${colorClass}-600`
            }`}>
              <IconComponent className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-semibold text-[var(--text-primary)] truncate">{activity.title}</h3>
              <p className="text-sm text-[var(--text-secondary)] capitalize">{activity.subject}</p>
            </div>
          </div>
          
          {completed && (
            <div className="flex items-center space-x-1 bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium flex-shrink-0">
              <Star className="w-3 h-3" />
              <span>Completed</span>
            </div>
          )}
        </div>

        <p className="text-[var(--text-secondary)] text-sm mb-4 line-clamp-2 flex-1">{activity.description}</p>

        {/* AI Generation Info */}
        <div className={`rounded-lg p-3 mb-4 ${
          theme.mode === 'high-contrast'
            ? 'bg-purple-100 border border-purple-500'
            : 'bg-gradient-to-r from-purple-50 to-blue-50'
        }`}>
          <div className="flex items-center space-x-2 mb-2">
            <Sparkles className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-medium text-purple-700">AI-Generated Activity</span>
          </div>
          <div className="text-xs text-gray-600 space-y-1">
            <div>Created: {formatTimeAgo(activity.created_at)}</div>
            {activity.ai_metadata && (
              <div>
                Template: {(activity.ai_metadata as Record<string, unknown>)?.template_used || 'Custom'}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4 mt-auto">
          <div className="flex items-center justify-between text-sm text-[var(--text-muted)]">
            <div className="flex items-center space-x-1">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-3 h-3 ${
                      i < activity.difficulty_level 
                        ? 'text-yellow-400 fill-current' 
                        : theme.mode === 'high-contrast' ? 'text-black' : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <span className="ml-1">{difficultyLabels[activity.difficulty_level as keyof typeof difficultyLabels]}</span>
            </div>
            
            <div className="flex items-center space-x-1">
              <Star className="w-3 h-3 text-orange-400" />
              <span>{activity.points_reward} pts</span>
            </div>
          </div>

          <button
            onClick={handleButtonClick}
            className={`w-full flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-medium transition-colors ${
              showViewContents
                ? theme.mode === 'high-contrast'
                  ? 'bg-gray-800 text-white hover:bg-black'
                  : 'bg-[var(--bg-tertiary)] text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)]'
                : completed
                ? theme.mode === 'high-contrast'
                  ? 'bg-gray-800 text-white hover:bg-black'
                  : 'bg-[var(--bg-tertiary)] text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)]'
                : theme.mode === 'high-contrast'
                ? `bg-[var(--accent-${colorClass})] text-white hover:opacity-90`
                : `bg-gradient-to-r from-${colorClass}-500 to-${colorClass}-600 text-white hover:opacity-90`
            }`}
          >
            {showViewContents ? (
              <>
                <Eye className="w-4 h-4 flex-shrink-0" />
                <span className="whitespace-nowrap">View Contents</span>
              </>
            ) : (
              <>
                <Eye className="w-4 h-4 flex-shrink-0" />
                <span className="whitespace-nowrap">{completed ? 'Play Again' : 'Start'}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}